import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { authAxios, QueryStatus } from "../../../utils";
import { RootState } from "../../../redux/store";
import {
  BadgeTransactionDisplayedApi,
  CreateMerchantCode,
  GetSupportingDocumentType,
  GetTransactionsAmountsPerPeriodType,
  PayloadForGetAuthorizationsRejectedByCompanyByPeriod,
  PayloadForGetAuthorizationsRejectedOfADepartmentByPeriod,
  PayloadForGetTransactionsByCompanyByPeriod,
  PayloadForGetTransactionsOfADepartmentByPeriod,
  PayloadForUpdateTransactionDetails,
  PublicAuthorizationType,
  PublicBadgeTransaction,
  PublicEmspCdrs,
  PublicTransactionsPropsType,
  PublicTransactionStatus,
  PublicTransactionType,
  SendSmsPayload,
  SupportingDocumentStatus,
  TransactionByPeriodType,
  TransactionType,
  UploadSupportingDocumentResponse,
} from "./transactionsAPI";
import axios from "axios";
import { VehiclesListType } from "../vehicles/vehiclesAPI";
import {
  listAllVehiclesInDbAsync,
  listVehiclesByDepartmentAsync,
} from "../vehicles/vehiclesSlice";

export interface TransactionsState {
  transactionsListStatus: QueryStatus;
  transactionsList: PublicTransactionsPropsType[] | null;
  authorizationsListStatus: QueryStatus;
  authorizationsList: PublicTransactionsPropsType[] | null;
  supportingDocumentUrl: string;
  isSupportingDocumentPdf: boolean;
  sendSmsStatus: QueryStatus;
  sendSmsReplaceInvalidStatus: QueryStatus;
  hasBackendError: boolean;
  lastReminderDateStatus: QueryStatus;
  lastReminderDate: Date | null;
  asfTransactionsListStatus: QueryStatus;
  asfTransactionsList: PublicBadgeTransaction[] | null;
  emspCdrsList: PublicEmspCdrs[] | null;
  emspCdrsListStatus: QueryStatus;
  transactionsAmountsPerPeriodStatus: QueryStatus;
  transactionsAmountsPerPeriod: GetTransactionsAmountsPerPeriodType | null;
  createMerchantCodeStatus: QueryStatus;
  transactionsListByPeriodStatus: QueryStatus;
  transactionsListByPeriod: TransactionByPeriodType[] | null;
  authorizationsRejectedByByPeriodListStatus: QueryStatus;
  authorizationsRejectedByByPeriodList: TransactionByPeriodType[] | null;
  uploadSupportingDocumentStatus: QueryStatus;
  updateSupportingDocumentStatus: QueryStatus;
  uploadProgress: number;
  updateTransactionDetailsStatus: QueryStatus;
}

const initialState: TransactionsState = {
  transactionsListStatus: "idle",
  transactionsList: null,
  authorizationsListStatus: "idle",
  authorizationsList: null,
  supportingDocumentUrl: "",
  isSupportingDocumentPdf: true,
  sendSmsStatus: "idle",
  sendSmsReplaceInvalidStatus: "idle",
  hasBackendError: false,
  lastReminderDateStatus: "idle",
  lastReminderDate: null,
  asfTransactionsListStatus: "idle",
  asfTransactionsList: null,
  transactionsAmountsPerPeriodStatus: "idle",
  transactionsAmountsPerPeriod: null,
  createMerchantCodeStatus: "idle",
  transactionsListByPeriodStatus: "idle",
  transactionsListByPeriod: null,
  authorizationsRejectedByByPeriodListStatus: "idle",
  authorizationsRejectedByByPeriodList: null,
  uploadSupportingDocumentStatus: "idle",
  updateSupportingDocumentStatus: "idle",
  uploadProgress: 0,
  updateTransactionDetailsStatus: "idle",
  emspCdrsList: null,
  emspCdrsListStatus: "idle",
};

export const transactionsListAsync = createAsyncThunk(
  "transactionsList/call",
  async (payload: { companyUuid: string }, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<Array<PublicTransactionType>>(
      `transactions/company/${payload.companyUuid}`,
    );
    return response.data.map(
      ({
        amount,
        executionDate,
        supportingDocumentUuid,
        status,
        rule,
        card,
        ...payload
      }) => {
        const isStatusPending =
          status === "PENDING_ADJUSTEMENT" ||
          status === "PENDING_CAPTURE" ||
          status === "PENDING_CONFIRMATION";

        const formattedStatus: PublicTransactionStatus =
          payload.type === "REFUND"
            ? "REFUND"
            : isStatusPending
              ? "PENDING"
              : status;

        return {
          ...payload,
          areSupportingDocumentsMandatory:
            payload.hasToRetrieveSupportingDocuments,
          amount:
            status === "CANCELED" || payload.type === "EXPENSE"
              ? -(amount / 100)
              : amount / 100,
          status: formattedStatus,
          supportingDocumentStatus:
            payload.hasToRetrieveSupportingDocuments &&
            supportingDocumentUuid &&
            status !== "CANCELED"
              ? "ACCEPTED"
              : payload.hasToRetrieveSupportingDocuments &&
                  status !== "CANCELED"
                ? ("MISSING" as SupportingDocumentStatus)
                : "",
          date: executionDate,
          time: executionDate,
          supportingDocumentUuid,
          ruleUuid: rule,
          cardUuid: card,
          cardLast4Digits: payload.cardLast4Digits ?? "",
          refundTransaction: payload.refundTransaction
            ? {
                ...payload.refundTransaction,
                amount: payload.refundTransaction.amount / 100,
              }
            : null,
          originalTransaction: payload.originalTransaction
            ? {
                ...payload.originalTransaction,
                amount: payload.originalTransaction.amount / 100,
              }
            : null,
          categoryAlreadyCorrected: null,
          merchantCity: payload.merchantPostalCode
            ? payload.merchantCity + ", " + payload.merchantPostalCode
            : payload.merchantCity,
        };
      },
    );
  },
);

export const transactionsListByPeriodAsync = createAsyncThunk(
  "transactionsListByPeriod/call",
  async (payload: PayloadForGetTransactionsByCompanyByPeriod, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<Array<PublicTransactionType>>(
      `transactions/company/${payload.companyUuid}`,
      {
        params: {
          from: payload.from,
          to: payload.to,
        },
      },
    );
    return response.data.map((r) => ({ ...r, date: r.executionDate }));
  },
);

export const asfTransactionsListAsync = createAsyncThunk(
  "asfTransactionsList/call",
  async (companyUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<BadgeTransactionDisplayedApi[]>(
      `asf_transactions/company/${companyUuid}`,
    );
    const allVehicles: VehiclesListType[] = await thunkAPI
      .dispatch(listAllVehiclesInDbAsync({ companyUuid }))
      .unwrap();

    return response.data.map((r) => {
      const formatVehicleInfo = (vehicle: {
        registrationNumber?: string;
        reference?: string | null;
      }) => {
        return [
          vehicle.registrationNumber,
          vehicle.reference?.toLocaleUpperCase(),
        ]
          .filter((d) => d)
          .join(" / ");
      };

      const vehicleInfo = formatVehicleInfo(r);
      const foundVehicle = allVehicles.find(
        (v) => formatVehicleInfo(v) === vehicleInfo,
      );

      return {
        ...r,
        date: r.executionDate,
        amount: r.amount
          ? r.type === "REFUND"
            ? -(r.amount / 100)
            : r.amount / 100
          : null,
        vehicleInfo,
        stationsInfo: `${r.entranceStationName}<br/>${r.exitStationName}`,
        vehicleAnalyticCode: foundVehicle?.analyticCode ?? null,
      };
    });
  },
);

export const asfTransactionsDepartmentListAsync = createAsyncThunk(
  "asfTransactionsDepartmentList/call",
  async (departmentUuid: string, thunkAPI) => {
    const axios = authAxios({ departmentUuid });
    const response = await axios.get<BadgeTransactionDisplayedApi[]>(
      `departments/asf_transactions/department/${departmentUuid}`,
    );
    const allVehicles: VehiclesListType[] = await thunkAPI
      .dispatch(listVehiclesByDepartmentAsync({ departmentUuid }))
      .unwrap();

    return response.data.map((r) => {
      const formatVehicleInfo = (vehicle: {
        registrationNumber?: string;
        reference?: string | null;
      }) => {
        return [
          vehicle.registrationNumber,
          vehicle.reference?.toLocaleUpperCase(),
        ]
          .filter((d) => d)
          .join(" / ");
      };

      const vehicleInfo = formatVehicleInfo(r);
      const foundVehicle = allVehicles.find(
        (v) => formatVehicleInfo(v) === vehicleInfo,
      );

      return {
        ...r,
        date: r.executionDate,
        amount: r.amount
          ? r.type === "REFUND"
            ? -(r.amount / 100)
            : r.amount / 100
          : null,
        vehicleInfo,
        stationsInfo: `${r.entranceStationName}<br/>${r.exitStationName}`,
        vehicleAnalyticCode: foundVehicle?.analyticCode ?? null,
      };
    });
  },
);

export const authorizationsListAsync = createAsyncThunk(
  "authorizationsRefusedList/call",
  async (companyUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<Array<PublicAuthorizationType>>(
      `authorizations_rejected/company/${companyUuid}`,
    );
    return response.data.map(({ executionDate, ...payload }) => {
      return {
        ...payload,
        driverName: [payload.driverFirstName, payload.driverLastName]
          .filter((d) => d)
          .join(" "),
        amount: null,
        status: "REJECTED" as PublicTransactionStatus,
        time: executionDate,
        date: executionDate,
        supportingDocumentStatus: "" as SupportingDocumentStatus,
        mileage: null,
        fuel: null,
        quantity: null,
        type: "EXPENSE" as TransactionType,
        refundTransaction: null,
        originalTransaction: null,
      };
    });
  },
);

export const authorizationsRejectedByCompanyByPeriodListAsync =
  createAsyncThunk(
    "authorizationsRejectedByCompanyByPeriodList/call",
    async (
      payload: PayloadForGetAuthorizationsRejectedByCompanyByPeriod,
      thunkAPI,
    ) => {
      const company = (thunkAPI.getState() as RootState).home.company;
      const axios = authAxios({ companyUuid: company?.uuid });
      const response = await axios.get<Array<PublicAuthorizationType>>(
        `authorizations_rejected/company/${payload.companyUuid}`,
        {
          params: {
            from: payload.from,
            to: payload.to,
          },
        },
      );
      return response.data.map((r) => ({ ...r, date: r.executionDate }));
    },
  );

export const transactionsOfADepartmentListAsync = createAsyncThunk(
  "transactionsForDepartmentUser/listTransactionsByDepartment",
  async (departmentUuid: string, thunkAPI) => {
    const department = (thunkAPI.getState() as RootState)
      .departmentsForDepartmentUser.currentDepartmentUuid;
    if (!department) {
      throw new Error("Department not selected");
    }

    const axios = authAxios({ departmentUuid: department });
    const response = await axios.get<Array<PublicTransactionType>>(
      `departments/transactions/department/${departmentUuid}`,
    );
    return response.data.map(
      ({
        amount,
        executionDate,
        supportingDocumentUuid,
        status,
        rule,
        card,
        ...payload
      }) => {
        const isStatusPending =
          status === "PENDING_ADJUSTEMENT" ||
          status === "PENDING_CAPTURE" ||
          status === "PENDING_CONFIRMATION";
        const formattedStatus: PublicTransactionStatus =
          payload.type === "REFUND"
            ? "REFUND"
            : isStatusPending
              ? "PENDING"
              : status;

        return {
          ...payload,
          areSupportingDocumentsMandatory:
            payload.hasToRetrieveSupportingDocuments,
          amount: status === "CANCELED" ? -(amount / 100) : amount / 100,
          status: formattedStatus,
          supportingDocumentStatus:
            payload.hasToRetrieveSupportingDocuments &&
            supportingDocumentUuid &&
            status !== "CANCELED"
              ? "ACCEPTED"
              : payload.hasToRetrieveSupportingDocuments &&
                  status !== "CANCELED"
                ? ("MISSING" as SupportingDocumentStatus)
                : "",
          date: executionDate,
          time: executionDate,
          supportingDocumentUuid,
          ruleUuid: rule,
          cardUuid: card,
          cardLast4Digits: payload.cardLast4Digits ?? "",
          refundTransaction: payload.refundTransaction
            ? {
                ...payload.refundTransaction,
                amount: payload.refundTransaction.amount / 100,
              }
            : null,
          originalTransaction: payload.originalTransaction
            ? {
                ...payload.originalTransaction,
                amount: payload.originalTransaction.amount / 100,
              }
            : null,
          categoryAlreadyCorrected: null,
        };
      },
    );
  },
);

export const transactionsListOfADepartmentByPeriodAsync = createAsyncThunk(
  "transactionsListOfADepartmentByPeriod/call",
  async (payload: PayloadForGetTransactionsOfADepartmentByPeriod) => {
    const axios = authAxios({ departmentUuid: payload.departmentUuid });
    const response = await axios.get<Array<PublicTransactionType>>(
      `departments/transactions/department/${payload.departmentUuid}`,
      {
        params: {
          from: payload.from,
          to: payload.to,
        },
      },
    );
    return response.data.map((r) => ({ ...r, date: r.executionDate }));
  },
);

export const authorizationsOfADepartmentListAsync = createAsyncThunk(
  "authorizationsRefusedForDepartmentList/call",
  async (departmentUuid: string, thunkAPI) => {
    const department = (thunkAPI.getState() as RootState)
      .departmentsForDepartmentUser.currentDepartmentUuid;
    if (!department) {
      throw new Error("Department not selected");
    }
    const axios = authAxios({ departmentUuid: department });
    const response = await axios.get<Array<PublicAuthorizationType>>(
      `departments/authorizations/rejected/department/${departmentUuid}`,
    );
    return response.data.map(({ executionDate, ...payload }) => {
      return {
        ...payload,
        driverName: [payload.driverFirstName, payload.driverLastName]
          .filter((d) => d)
          .join(" "),
        amount: null,
        status: "REJECTED" as PublicTransactionStatus,
        time: executionDate,
        date: executionDate,
        supportingDocumentStatus: "" as SupportingDocumentStatus,
        mileage: null,
        fuel: null,
        quantity: null,
        type: "EXPENSE" as TransactionType,
        refundTransaction: null,
        originalTransaction: null,
      };
    });
  },
);

export const authorizationsRejectedOfADepartmentByPeriodListAsync =
  createAsyncThunk(
    "authorizationsRejectedOfADepartmentByPeriodList/call",
    async (
      payload: PayloadForGetAuthorizationsRejectedOfADepartmentByPeriod,
    ) => {
      const axios = authAxios({ departmentUuid: payload.departmentUuid });
      const response = await axios.get<Array<PublicAuthorizationType>>(
        `departments/authorizations/rejected/department/${payload.departmentUuid}`,
      );
      return response.data.map((r) => ({ ...r, date: r.executionDate }));
    },
  );

export const getSupportingDocument = createAsyncThunk(
  "getSupportingDocument/call",
  async (uuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.post<GetSupportingDocumentType>(
      `supporting_documents/${uuid}/get_presigned_url`,
    );
    void thunkAPI.dispatch(createUrlSupportingDocumentAsync(response.data));
  },
);

export const getSupportingDocumentForDepartment = createAsyncThunk(
  "getSupportingDocumentForDepartment/call",
  async (uuid: string, thunkAPI) => {
    const department = (thunkAPI.getState() as RootState)
      .departmentsForDepartmentUser.currentDepartmentUuid;
    if (!department) {
      throw new Error("Department not selected");
    }
    const axios = authAxios({ departmentUuid: department });
    const response = await axios.post<GetSupportingDocumentType>(
      `departments/supporting_documents/${uuid}/get_presigned_url`,
    );
    void thunkAPI.dispatch(createUrlSupportingDocumentAsync(response.data));
  },
);

export const sendSmsAsync = createAsyncThunk(
  "sendSms/call",
  async (payload: SendSmsPayload, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(`send_sms_reminder_supporting_document`, payload);
    void thunkAPI.dispatch(
      getLastReminderDateAsync({ transaction: payload.transactionUuid }),
    );
  },
);

export const sendSmsForDepartementAsync = createAsyncThunk(
  "sendSmsForDepartement/call",
  async (payload: SendSmsPayload, thunkAPI) => {
    const department = (thunkAPI.getState() as RootState)
      .departmentsForDepartmentUser.currentDepartmentUuid;
    if (!department) {
      throw new Error("Department not selected");
    }
    const axios = authAxios({ departmentUuid: department });
    await axios.post(
      `departments/transactions/send_sms_reminder_supporting_document`,
      payload,
    );
    void thunkAPI.dispatch(
      getLastReminderDateAsync({ transaction: payload.transactionUuid }),
    );
  },
);

export const sendSmsReplaceInvalidAsync = createAsyncThunk(
  "sendSmsReplaceInvalid/call",
  async (payload: SendSmsPayload, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(`send_sms_replace_invalid_supporting_document`, payload);
  },
);

export const sendSmsReplaceInvalidForDepartmentAsync = createAsyncThunk(
  "sendSmsReplaceInvalidForDepartment/call",
  async (payload: SendSmsPayload, thunkAPI) => {
    const department = (thunkAPI.getState() as RootState)
      .departmentsForDepartmentUser.currentDepartmentUuid;
    if (!department) {
      throw new Error("Department not selected");
    }
    const axios = authAxios({ departmentUuid: department });
    await axios.post(
      `departments/transactions/send_sms_replace_invalid_supporting_document`,
      payload,
    );
  },
);

export const getLastReminderDateAsync = createAsyncThunk(
  "GetLastReminderDate/call",
  async (payload: { transaction: string }, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<{ date: string }>(
      `transaction/${payload.transaction}/get_last_reminder_date`,
    );
    return { date: response.data.date ? new Date(response.data.date) : null };
  },
);

export const getLastReminderDateForDepartmentAsync = createAsyncThunk(
  "GetLastReminderDateForDepartment/call",
  async (payload: { transaction: string }, thunkAPI) => {
    const department = (thunkAPI.getState() as RootState)
      .departmentsForDepartmentUser.currentDepartmentUuid;
    if (!department) {
      throw new Error("Department not selected");
    }
    const axios = authAxios({ departmentUuid: department });
    const response = await axios.get<{ date: string }>(
      `departments/transaction/${payload.transaction}/get_last_reminder_date`,
    );
    return { date: response.data.date ? new Date(response.data.date) : null };
  },
);

export const createUrlSupportingDocumentAsync = createAsyncThunk(
  "createUrlSupportingDocument/call",
  async (supportingDocument: GetSupportingDocumentType, thunkAPI) => {
    if (supportingDocument.presignedUrl && supportingDocument.fileName) {
      const fileNameSplit = supportingDocument.fileName.split(".");
      const extension = fileNameSplit[fileNameSplit.length - 1];
      const response = await axios({
        url: supportingDocument.presignedUrl,
        method: "GET",
        responseType: "arraybuffer",
      });
      let url = "";
      if (extension === "pdf") {
        url = window.URL.createObjectURL(
          new Blob([response.data], { type: "application/pdf" }),
        );
        thunkAPI.dispatch(
          transactionsSlice.actions.setIsSupportingDocumentPdf(),
        );
      } else {
        url = window.URL.createObjectURL(
          new Blob([response.data], { type: `image/${extension}` }),
        );
      }
      thunkAPI.dispatch(
        transactionsSlice.actions.setSupportingDocumentUrl({ url }),
      );
    }
  },
);

export const getTransactionsAmountsPerPeriodAsync = createAsyncThunk(
  "getTransactionsAmountsPerPeriod/call",
  async (payload: { companyUuid: string; date: string }, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<GetTransactionsAmountsPerPeriodType>(
      `transactions/company/${payload.companyUuid}/amounts_per_period/${payload.date}`,
    );
    return response.data;
  },
);

export const createMerchantCodeAsync = createAsyncThunk(
  "merchantCode/create",
  async (payload: CreateMerchantCode, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(
      `authorizations/merchant_code/company/${payload.companyUuid}`,
      {
        authorizationUuid: payload.authorizationUuid,
        category: payload.category,
      },
    );
  },
);

export const uploadSupportingDocumentAsync = createAsyncThunk(
  "supportingDocument/upload",
  async (payload: FormData, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.post<UploadSupportingDocumentResponse>(
      `supporting_documents/upload`,
      payload,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const progress = (progressEvent.progress ?? 0) * 100;
          thunkAPI.dispatch(
            transactionsSlice.actions.setUploadProgress(progress),
          );
        },
      },
    );
    void thunkAPI.dispatch(
      updateSupportingDocumentStatus(response.data.supportingDocumentId),
    );
  },
);

export const updateSupportingDocumentStatus = createAsyncThunk(
  "updateSupportingDocument/call",
  async (uuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });

    await axios.post(`supporting_documents/${uuid}/validate_status`);
  },
);

export const updateMileageAsync = createAsyncThunk(
  "updateMileage/call",
  async (payload: PayloadForUpdateTransactionDetails, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(
      `transaction/${payload.transactionUuid}/update_mileage`,
      payload,
    );
  },
);

export const emspCdrsListAsync = createAsyncThunk(
  "emspCdrsList/call",
  async (payload: { companyUuid: string }, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    return (
      await axios.get<Array<PublicEmspCdrs>>(
        `emsp/cdrs/company/${payload.companyUuid}`,
      )
    ).data;
  },
);

export const transactionsSlice = createSlice({
  name: "transactions",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetAsfTransactionsListStatus(state) {
      state.asfTransactionsListStatus = "idle";
    },
    resetTransactionsListStatus(state) {
      state.transactionsListStatus = "idle";
    },
    resetAuthorizationsListStatus(state) {
      state.authorizationsListStatus = "idle";
    },
    resetEmspCdrsListStatus(state) {
      state.emspCdrsListStatus = "idle";
    },
    resetSupportingDocumentUrl(state) {
      state.supportingDocumentUrl = "";
      state.isSupportingDocumentPdf = false;
    },
    setIsSupportingDocumentPdf(state) {
      state.isSupportingDocumentPdf = true;
    },
    setSupportingDocumentUrl(state, action: PayloadAction<{ url: string }>) {
      state.supportingDocumentUrl = action.payload.url;
    },
    setHasBackendError(state) {
      state.hasBackendError = true;
    },
    resetUploadStatus(state) {
      state.uploadSupportingDocumentStatus = "idle";
      state.uploadProgress = 0;
    },
    resetUpdateSupportingDocumentStatus(state) {
      state.updateSupportingDocumentStatus = "idle";
    },
    setUploadProgress(state, action: { payload: number }) {
      state.uploadProgress = action.payload;
    },
    resetUpdateTransactionDetailsStatus(state) {
      state.updateTransactionDetailsStatus = "idle";
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(transactionsListAsync.pending, (state) => {
        state.transactionsListStatus = "processing";
        state.supportingDocumentUrl = "";
        state.isSupportingDocumentPdf = false;
      })
      .addCase(transactionsListAsync.fulfilled, (state, action) => {
        state.transactionsList = action.payload;
        state.transactionsListStatus = "success";
        state.sendSmsReplaceInvalidStatus = "idle";
      })
      .addCase(transactionsListAsync.rejected, (state) => {
        state.transactionsListStatus = "failed";
        state.transactionsList = [];
      })
      .addCase(authorizationsListAsync.pending, (state) => {
        state.authorizationsListStatus = "processing";
      })
      .addCase(authorizationsListAsync.fulfilled, (state, action) => {
        state.authorizationsList = action.payload;
        state.createMerchantCodeStatus = "idle";
        state.authorizationsListStatus = "success";
      })
      .addCase(authorizationsListAsync.rejected, (state) => {
        state.authorizationsListStatus = "failed";
        state.authorizationsList = [];
      })
      .addCase(transactionsOfADepartmentListAsync.pending, (state) => {
        state.transactionsListStatus = "processing";
        state.supportingDocumentUrl = "";
        state.isSupportingDocumentPdf = false;
      })
      .addCase(
        transactionsOfADepartmentListAsync.fulfilled,
        (state, action) => {
          state.transactionsList = action.payload;
          state.transactionsListStatus = "success";
          state.sendSmsReplaceInvalidStatus = "idle";
        },
      )
      .addCase(transactionsOfADepartmentListAsync.rejected, (state) => {
        state.transactionsListStatus = "failed";
        state.transactionsList = [];
      })
      .addCase(authorizationsOfADepartmentListAsync.pending, (state) => {
        state.authorizationsListStatus = "processing";
      })
      .addCase(
        authorizationsOfADepartmentListAsync.fulfilled,
        (state, action) => {
          state.authorizationsList = action.payload;
          state.createMerchantCodeStatus = "idle";
          state.authorizationsListStatus = "success";
        },
      )
      .addCase(authorizationsOfADepartmentListAsync.rejected, (state) => {
        state.authorizationsListStatus = "failed";
        state.authorizationsList = [];
      })
      .addCase(sendSmsAsync.pending, (state) => {
        state.sendSmsStatus = "processing";
      })
      .addCase(sendSmsAsync.fulfilled, (state) => {
        state.sendSmsStatus = "success";
      })
      .addCase(sendSmsAsync.rejected, (state) => {
        state.sendSmsStatus = "failed";
      })
      .addCase(sendSmsForDepartementAsync.pending, (state) => {
        state.sendSmsStatus = "processing";
      })
      .addCase(sendSmsForDepartementAsync.fulfilled, (state) => {
        state.sendSmsStatus = "success";
      })
      .addCase(sendSmsForDepartementAsync.rejected, (state) => {
        state.sendSmsStatus = "failed";
      })
      .addCase(sendSmsReplaceInvalidAsync.pending, (state) => {
        state.sendSmsReplaceInvalidStatus = "processing";
      })
      .addCase(sendSmsReplaceInvalidAsync.fulfilled, (state) => {
        state.sendSmsReplaceInvalidStatus = "success";
      })
      .addCase(sendSmsReplaceInvalidAsync.rejected, (state) => {
        state.sendSmsReplaceInvalidStatus = "failed";
      })
      .addCase(sendSmsReplaceInvalidForDepartmentAsync.pending, (state) => {
        state.sendSmsReplaceInvalidStatus = "processing";
      })
      .addCase(sendSmsReplaceInvalidForDepartmentAsync.fulfilled, (state) => {
        state.sendSmsReplaceInvalidStatus = "success";
      })
      .addCase(sendSmsReplaceInvalidForDepartmentAsync.rejected, (state) => {
        state.sendSmsReplaceInvalidStatus = "failed";
      })
      .addCase(getLastReminderDateAsync.pending, (state) => {
        state.lastReminderDateStatus = "processing";
      })
      .addCase(getLastReminderDateAsync.fulfilled, (state, action) => {
        state.lastReminderDate = action.payload.date;
        state.lastReminderDateStatus = "success";
      })
      .addCase(getLastReminderDateAsync.rejected, (state) => {
        state.lastReminderDateStatus = "failed";
      })
      .addCase(getLastReminderDateForDepartmentAsync.pending, (state) => {
        state.lastReminderDateStatus = "processing";
      })
      .addCase(
        getLastReminderDateForDepartmentAsync.fulfilled,
        (state, action) => {
          state.lastReminderDate = action.payload.date;

          state.lastReminderDateStatus = "success";
        },
      )
      .addCase(getLastReminderDateForDepartmentAsync.rejected, (state) => {
        state.lastReminderDateStatus = "failed";
      })

      .addCase(asfTransactionsListAsync.pending, (state) => {
        state.asfTransactionsListStatus = "processing";
      })
      .addCase(asfTransactionsListAsync.fulfilled, (state, action) => {
        state.asfTransactionsList = action.payload;
        state.asfTransactionsListStatus = "success";
      })
      .addCase(asfTransactionsListAsync.rejected, (state) => {
        state.asfTransactionsListStatus = "failed";
      })
      .addCase(asfTransactionsDepartmentListAsync.pending, (state) => {
        state.asfTransactionsListStatus = "processing";
      })
      .addCase(
        asfTransactionsDepartmentListAsync.fulfilled,
        (state, action) => {
          state.asfTransactionsList = action.payload;
          state.asfTransactionsListStatus = "success";
        },
      )
      .addCase(asfTransactionsDepartmentListAsync.rejected, (state) => {
        state.asfTransactionsListStatus = "failed";
      })
      .addCase(getTransactionsAmountsPerPeriodAsync.pending, (state) => {
        state.transactionsAmountsPerPeriodStatus = "processing";
      })
      .addCase(
        getTransactionsAmountsPerPeriodAsync.fulfilled,
        (state, action) => {
          state.transactionsAmountsPerPeriod = action.payload;
          state.transactionsAmountsPerPeriodStatus = "success";
        },
      )
      .addCase(getTransactionsAmountsPerPeriodAsync.rejected, (state) => {
        state.transactionsAmountsPerPeriodStatus = "failed";
      })
      .addCase(createMerchantCodeAsync.pending, (state) => {
        state.createMerchantCodeStatus = "processing";
      })
      .addCase(createMerchantCodeAsync.fulfilled, (state) => {
        state.createMerchantCodeStatus = "success";
      })
      .addCase(createMerchantCodeAsync.rejected, (state) => {
        state.createMerchantCodeStatus = "failed";
      })
      .addCase(transactionsListByPeriodAsync.pending, (state) => {
        state.transactionsListByPeriodStatus = "processing";
      })
      .addCase(transactionsListByPeriodAsync.fulfilled, (state, action) => {
        state.transactionsListByPeriod = action.payload;
        state.transactionsListByPeriodStatus = "success";
      })
      .addCase(transactionsListByPeriodAsync.rejected, (state) => {
        state.transactionsListByPeriodStatus = "failed";
      })
      .addCase(transactionsListOfADepartmentByPeriodAsync.pending, (state) => {
        state.transactionsListByPeriodStatus = "processing";
      })
      .addCase(
        transactionsListOfADepartmentByPeriodAsync.fulfilled,
        (state, action) => {
          state.transactionsListByPeriod = action.payload;
          state.transactionsListByPeriodStatus = "success";
        },
      )
      .addCase(transactionsListOfADepartmentByPeriodAsync.rejected, (state) => {
        state.transactionsListByPeriodStatus = "failed";
      })
      .addCase(
        authorizationsRejectedByCompanyByPeriodListAsync.pending,
        (state) => {
          state.authorizationsRejectedByByPeriodListStatus = "processing";
        },
      )
      .addCase(
        authorizationsRejectedByCompanyByPeriodListAsync.fulfilled,
        (state, action) => {
          state.authorizationsRejectedByByPeriodList = action.payload;
          state.authorizationsRejectedByByPeriodListStatus = "success";
        },
      )
      .addCase(
        authorizationsRejectedByCompanyByPeriodListAsync.rejected,
        (state) => {
          state.authorizationsRejectedByByPeriodListStatus = "failed";
        },
      )
      .addCase(
        authorizationsRejectedOfADepartmentByPeriodListAsync.pending,
        (state) => {
          state.authorizationsRejectedByByPeriodListStatus = "processing";
        },
      )
      .addCase(
        authorizationsRejectedOfADepartmentByPeriodListAsync.fulfilled,
        (state, action) => {
          state.authorizationsRejectedByByPeriodList = action.payload;
          state.authorizationsRejectedByByPeriodListStatus = "success";
        },
      )
      .addCase(
        authorizationsRejectedOfADepartmentByPeriodListAsync.rejected,
        (state) => {
          state.authorizationsRejectedByByPeriodListStatus = "failed";
        },
      )
      .addCase(uploadSupportingDocumentAsync.pending, (state) => {
        state.uploadSupportingDocumentStatus = "processing";
      })
      .addCase(uploadSupportingDocumentAsync.fulfilled, (state) => {
        state.uploadSupportingDocumentStatus = "success";
      })
      .addCase(uploadSupportingDocumentAsync.rejected, (state) => {
        state.uploadSupportingDocumentStatus = "failed";
      })
      .addCase(updateSupportingDocumentStatus.pending, (state) => {
        state.updateSupportingDocumentStatus = "processing";
      })
      .addCase(updateSupportingDocumentStatus.fulfilled, (state) => {
        state.updateSupportingDocumentStatus = "success";
        state.transactionsListStatus = "idle";
      })
      .addCase(updateSupportingDocumentStatus.rejected, (state) => {
        state.updateSupportingDocumentStatus = "failed";
      })
      .addCase(updateMileageAsync.pending, (state) => {
        state.updateTransactionDetailsStatus = "processing";
      })
      .addCase(updateMileageAsync.fulfilled, (state) => {
        state.updateTransactionDetailsStatus = "success";
      })
      .addCase(updateMileageAsync.rejected, (state) => {
        state.updateTransactionDetailsStatus = "failed";
      })
      .addCase(emspCdrsListAsync.pending, (state) => {
        state.emspCdrsListStatus = "processing";
      })
      .addCase(emspCdrsListAsync.fulfilled, (state, action) => {
        state.emspCdrsList = action.payload;
        state.emspCdrsListStatus = "success";
      })
      .addCase(emspCdrsListAsync.rejected, (state) => {
        state.emspCdrsListStatus = "failed";
      });
  },
});

export const selectTransactionsList = (state: RootState) =>
  state.transactions.transactionsList;
export const selectTransactionsListStatus = (state: RootState) =>
  state.transactions.transactionsListStatus;

export const selectAsfTransactionsList = (state: RootState) =>
  state.transactions.asfTransactionsList;
export const selectAsfTransactionsListStatus = (state: RootState) =>
  state.transactions.asfTransactionsListStatus;

export const selectAuthorizationsList = (state: RootState) =>
  state.transactions.authorizationsList;
export const selectAuthorizationsListStatus = (state: RootState) =>
  state.transactions.authorizationsListStatus;

export const selectSupportingDocumentUrl = (state: RootState) =>
  state.transactions.supportingDocumentUrl;

export const selectIsSupportingDocumentPdf = (state: RootState) =>
  state.transactions.isSupportingDocumentPdf;

export const selectSendSmsStatus = (state: RootState) =>
  state.transactions.sendSmsStatus;

export const selectSendSmsReplaceInvalidStatus = (state: RootState) =>
  state.transactions.sendSmsReplaceInvalidStatus;

export const selectHasBackendError = (state: RootState) =>
  state.transactions.hasBackendError;
export const selectLastReminderDate = (state: RootState) =>
  state.transactions.lastReminderDate;
export const selectLastReminderDateStatus = (state: RootState) =>
  state.transactions.lastReminderDateStatus;

export const selectTransactionsAmountsPerPeriod = (state: RootState) =>
  state.transactions.transactionsAmountsPerPeriod;
export const selectTransactionsAmountsPerPeriodStatus = (state: RootState) =>
  state.transactions.transactionsAmountsPerPeriodStatus;

export const selectCreateMerchantCodeStatus = (state: RootState) =>
  state.transactions.createMerchantCodeStatus;

export const selectTransactionsListByPeriod = (state: RootState) =>
  state.transactions.transactionsListByPeriod;
export const selectTransactionsListByPeriodStatus = (state: RootState) =>
  state.transactions.transactionsListByPeriodStatus;

export const selectAuthorizationsRejectedByByPeriodList = (state: RootState) =>
  state.transactions.authorizationsRejectedByByPeriodList;
export const selectAuthorizationsRejectedByByPeriodListStatus = (
  state: RootState,
) => state.transactions.authorizationsRejectedByByPeriodListStatus;

export const selectUploadSupportingDocumentStatus = (state: RootState) =>
  state.transactions.uploadSupportingDocumentStatus;

export const selectUpdateSupportingDocumentStatus = (state: RootState) =>
  state.transactions.updateSupportingDocumentStatus;

export const selectUploadProgress = (state: RootState) =>
  state.transactions.uploadProgress;

export const selectUpdateTransactionDetailsStatus = (state: RootState) =>
  state.transactions.updateTransactionDetailsStatus;

export const selectEmspCdrsList = (state: RootState) =>
  state.transactions.emspCdrsList;
export const selectEmspCdrsListStatus = (state: RootState) =>
  state.transactions.emspCdrsListStatus;

export default transactionsSlice.reducer;
